import { useState, useEffect } from 'react';
import { Box, Typography, Chip, IconButton, Tooltip } from '@mui/material';
import {
    asyncGetQuestions,
    asyncUpdateQuestion,
    asyncGetAnswers,
} from '../../../../../../../services/survey.service';
import { ToastSwal } from '../../../../../../components/general/toastSwals/toastSwal';
import { ModalQuestion } from './modalQuestion';
import { ModalRange } from './modalRange';
import { ModalAnswers } from './modalAnswers';
import { Table } from '../../../../../../components/general/table';
import EditIcon from '@mui/icons-material/Edit';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import LinearScaleIcon from '@mui/icons-material/LinearScale';
import QuestionAnswerIcon from '@mui/icons-material/QuestionAnswer';
import Swal from 'sweetalert2';

interface Props {
    idSurvey?: string;
    saveQuestions: (callback: () => void) => void;
}

const typesSupported: { [key: string]: boolean } = {
    linear: true,
    single: true,
    multiple: true,
    'list-only': true,
    'linear-label': true,
};

export const SurveyQuestions = ({ idSurvey, saveQuestions }: Props) => {
    const [questions, setQuestions] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [openModal, setOpenModal] = useState(false);
    const [selectedQuestion, setSelectedQuestion] = useState<any>(null);
    const [openRange, setOpenRange] = useState(false);
    const [openAnswers, setOpenAnswers] = useState(false);
    const [selectedQuestionForAnswers, setSelectedQuestionForAnswers] =
        useState<any>(null);

    const loadQuestions = async () => {
        if (!idSurvey) return;
        setLoading(true);
        try {
            const data = await asyncGetQuestions(idSurvey);
            // Cargar respuestas para cada pregunta
            const questionsWithAnswers = await Promise.all(
                data.map(async (question: any) => {
                    try {
                        if (typesSupported[question.type]) {
                            const answers = await asyncGetAnswers(question.id);
                            return { ...question, answers };
                        }
                        return question;
                    } catch (error) {
                        console.error(
                            `Error loading answers for question ${question.id}:`,
                            error
                        );
                        // Si falla la carga de respuestas, devolver la pregunta sin respuestas
                        return { ...question, answers: [] };
                    }
                })
            );
            setQuestions(questionsWithAnswers);
        } catch (error) {
            console.error('Error loading questions:', error);
            ToastSwal.fire({
                text: 'Error al cargar las preguntas',
                icon: 'error',
                timer: 3000,
                position: 'center-end',
            });
        } finally {
            setLoading(false);
        }
    };

    const handleAddQuestion = () => {
        if (!idSurvey) {
            console.warn('No se puede abrir la modal sin un ID de encuesta');
            return;
        }
        setSelectedQuestion(null);
        setOpenModal(true);
    };

    const handleEditQuestion = async (questionId: string) => {
        setLoading(true);
        try {
            // Buscar la pregunta en el array local
            const question = questions.find((q) => q.id === questionId);
            setSelectedQuestion(question);
            setOpenModal(true);
        } catch (error) {
            console.error('Error al cargar pregunta:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleToggleStatus = async (
        questionId: string,
        currentStatus: string
    ) => {
        const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
        const actionText = newStatus === 'active' ? 'activar' : 'desactivar';

        const result = await Swal.fire({
            title: `¿${
                actionText.charAt(0).toUpperCase() + actionText.slice(1)
            } pregunta?`,
            text: `¿Estás seguro de que deseas ${actionText} esta pregunta?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: `Sí, ${actionText}`,
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#6a1b9a',
            cancelButtonColor: '#757575',
        });

        if (result.isConfirmed) {
            try {
                await asyncUpdateQuestion(questionId, { status: newStatus });
                ToastSwal.fire({
                    text: `Pregunta ${
                        newStatus === 'active' ? 'activada' : 'desactivada'
                    } exitosamente`,
                    icon: 'success',
                    timer: 3000,
                    position: 'center-end',
                });
                loadQuestions();
            } catch (error) {
                ToastSwal.fire({
                    text: 'Error al cambiar el estado de la pregunta',
                    icon: 'error',
                    timer: 3000,
                    position: 'center-end',
                });
            }
        }
    };

    const handleOpenAnswers = (question: any) => {
        setSelectedQuestionForAnswers(question);
        if (question.type === 'linear') {
            // Para tipo linear, abrir modal de rango
            setOpenRange(true);
        } else {
            // Para tipo multiple u otros, abrir modal de respuestas
            setOpenAnswers(true);
        }
    };

    const handleCloseRange = () => {
        setOpenRange(false);
        setSelectedQuestionForAnswers(null);
        // Siempre recargar las preguntas al cerrar el modal de rango
        loadQuestions();
    };

    const handleCloseAnswers = () => {
        setOpenAnswers(false);
        setSelectedQuestionForAnswers(null);
        // Siempre recargar las preguntas al cerrar el modal de respuestas
        loadQuestions();
    };

    const handleModalClose = (reload?: boolean) => {
        setOpenModal(false);
        setSelectedQuestion(null);
        if (reload) {
            loadQuestions();
        }
    };

    useEffect(() => {
        // Solo registrar la función, no ejecutarla
        saveQuestions(() => handleAddQuestion);
    }, [idSurvey]);

    useEffect(() => {
        if (idSurvey) {
            loadQuestions();
        }
    }, [idSurvey]);

    const columns: any[] = [
        {
            field: 'description',
            headerName: 'Descripción',
            minWidth: 250,
            flex: 2,
        },
        {
            field: 'type',
            headerName: 'Tipo Pregunta',
            minWidth: 120,
            flex: 0.8,
            renderCell: (params: any) => {
                const typeMap: { [key: string]: string } = {
                    text: 'Texto',
                    date: 'Fecha',
                    single: 'Opción única',
                    multiple: 'Opción múltiple',
                    linear: 'Escala lineal',
                    'list-only': 'Lista',
                    'linear-label': 'Escala con etiquetas',
                };
                return typeMap[params.value] || params.value;
            },
        },
        {
            field: 'subtype',
            headerName: 'Subtipo Pregunta',
            minWidth: 120,
            flex: 0.8,
            renderCell: (params: any) => params.value || '-',
        },
        {
            field: 'answers',
            headerName: 'Respuesta(s)',
            minWidth: 100,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) =>
                typesSupported[params.row.type] && (
                    <Tooltip
                        title={
                            params.row.type === 'linear'
                                ? 'Rango'
                                : 'Respuesta(s)'
                        }
                    >
                        <IconButton
                            size="small"
                            onClick={() => handleOpenAnswers(params.row)}
                        >
                            {params.row.type === 'linear' ? (
                                <LinearScaleIcon
                                    sx={{ color: 'primary.main' }}
                                />
                            ) : (
                                <QuestionAnswerIcon
                                    sx={{ color: 'primary.main' }}
                                />
                            )}
                        </IconButton>
                    </Tooltip>
                ),
        },
        {
            field: 'why',
            headerName: 'Porque',
            minWidth: 100,
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
                <Chip
                    label={params.value ? 'ACTIVO' : 'INACTIVO'}
                    size="small"
                    color={params.value ? 'success' : 'error'}
                />
            ),
        },
        {
            field: 'dependency',
            headerName: 'Dependencia',
            minWidth: 110,
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
                <Chip
                    label={params.value ? 'ACTIVO' : 'INACTIVO'}
                    size="small"
                    color={params.value ? 'success' : 'error'}
                />
            ),
        },
        {
            field: 'status',
            headerName: 'Estado',
            minWidth: 100,
            flex: 0.5,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => {
                const statusMap: {
                    [key: string]: { label: string; color: any };
                } = {
                    active: { label: 'ACTIVO', color: 'success' },
                    inactive: { label: 'INACTIVO', color: 'error' },
                };
                const status = statusMap[params.value] || {
                    label: 'EN PROCESO',
                    color: 'info',
                };
                return (
                    <Chip
                        label={status.label}
                        size="small"
                        color={status.color}
                    />
                );
            },
        },
        {
            field: 'actions',
            headerName: 'Acciones',
            minWidth: 100,
            sortable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params: any) => (
                <>
                    <Tooltip title="Editar">
                        <IconButton
                            size="small"
                            onClick={() => handleEditQuestion(params.row.id)}
                        >
                            <EditIcon sx={{ color: 'primary.main' }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip
                        title={
                            params.row.status === 'active'
                                ? 'Desactivar'
                                : 'Activar'
                        }
                    >
                        <IconButton
                            size="small"
                            onClick={() =>
                                handleToggleStatus(
                                    params.row.id,
                                    params.row.status
                                )
                            }
                        >
                            {params.row.status === 'active' ? (
                                <ToggleOnIcon sx={{ color: 'primary.main' }} />
                            ) : (
                                <ToggleOffIcon sx={{ color: 'primary.main' }} />
                            )}
                        </IconButton>
                    </Tooltip>
                </>
            ),
        },
    ];

    if (!idSurvey) {
        return (
            <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                    Primero debes guardar la información de la encuesta
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            {questions.length === 0 && !loading ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                        No hay preguntas creadas. Haz clic en "Agregar Pregunta"
                        para comenzar.
                    </Typography>
                </Box>
            ) : (
                <Table
                    columns={columns}
                    rows={questions}
                    loading={loading}
                    autoAdjustHeight
                    disableRowSelectionOnClick
                />
            )}

            {openModal && (
                <ModalQuestion
                    open={openModal}
                    onClose={handleModalClose}
                    surveyId={idSurvey}
                    question={selectedQuestion}
                    questionsDependency={questions}
                />
            )}

            {openRange && selectedQuestionForAnswers && (
                <ModalRange
                    open={openRange}
                    onClose={handleCloseRange}
                    surveyId={idSurvey}
                    questionId={selectedQuestionForAnswers.id}
                    answer={selectedQuestionForAnswers.answers?.[0]}
                />
            )}

            {openAnswers && selectedQuestionForAnswers && (
                <ModalAnswers
                    open={openAnswers}
                    onClose={handleCloseAnswers}
                    surveyId={idSurvey}
                    questionId={selectedQuestionForAnswers.id}
                    type={selectedQuestionForAnswers.type}
                />
            )}
        </Box>
    );
};
